/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativos, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaSmartPhones.module.scss";

// Datos IPSOS Smartphones
const IPSOS_DATA = {
  tieneSmartphone: 0.954, // 95.4%
  tieneCorporativo: 0.362, // 36.2% tiene teléfono corporativo
  frecuenciaRenovacion: {
    cadaAno: 0.161, // 16.1%
    cada2Anos: 0.412, // 41.2%
    cada3AnosMas: 0.417, // 41.7%
    cada4Anos: 0.011, // 1.1%
  },
  marcas: {
    Apple: 0.509,
    Samsung: 0.318,
    Xiaomi: 0.082,
    Honor: 0.037,
    Motorola: 0.023,
    Otros: 0.031,
  },
};

// Precios por gama
const PRECIOS_GAMA_ALTA = [1000, 1200, 1400, 1600];
const PRECIOS_GAMA_MEDIA = [500, 600, 750, 900];

// Escenarios de captación
const ESCENARIOS_CAPTACION = [3, 5, 10, 20];

export const SmartPhones = () => {
  const { formatNumber, formatUSD, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("Samsung");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(3);
  const [verMensual, setVerMensual] = useState(false);
  const [filtroGamaAlta, setFiltroGamaAlta] = useState(false);
  const [filtroGamaMedia, setFiltroGamaMedia] = useState(false);
  const [precioGamaAlta, setPrecioGamaAlta] = useState(1200);
  const [precioGamaMedia, setPrecioGamaMedia] = useState(750);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativos({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // Calcular mercado con IPSOS
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const conSmartphone = Math.round(totalEjecutivos * IPSOS_DATA.tieneSmartphone);
    const conCorporativo = Math.round(totalEjecutivos * IPSOS_DATA.tieneCorporativo);
    const smartphonesTotales = conSmartphone + conCorporativo;

    const pctGamaAlta = IPSOS_DATA.frecuenciaRenovacion.cadaAno + IPSOS_DATA.frecuenciaRenovacion.cada2Anos;
    const pctGamaMedia = IPSOS_DATA.frecuenciaRenovacion.cada3AnosMas + IPSOS_DATA.frecuenciaRenovacion.cada4Anos;

    const smartphonesGamaAlta = Math.round(smartphonesTotales * pctGamaAlta);
    const smartphonesGamaMedia = Math.round(smartphonesTotales * pctGamaMedia);

    const frecuenciaGamaAlta = 1.5;
    const frecuenciaGamaMedia = 3.5;

    const unidadesAnualesGamaAlta = Math.round(smartphonesGamaAlta / frecuenciaGamaAlta);
    const unidadesAnualesGamaMedia = Math.round(smartphonesGamaMedia / frecuenciaGamaMedia);

    const mercadoGamaAlta = unidadesAnualesGamaAlta * precioGamaAlta;
    const mercadoGamaMedia = unidadesAnualesGamaMedia * precioGamaMedia;

    return {
      conSmartphone,
      conCorporativo,
      smartphonesTotales,
      gamaAlta: {
        smartphones: smartphonesGamaAlta,
        pct: pctGamaAlta,
        unidadesAnuales: unidadesAnualesGamaAlta,
        mercado: mercadoGamaAlta,
      },
      gamaMedia: {
        smartphones: smartphonesGamaMedia,
        pct: pctGamaMedia,
        unidadesAnuales: unidadesAnualesGamaMedia,
        mercado: mercadoGamaMedia,
      },
      mercadoTotal: mercadoGamaAlta + mercadoGamaMedia,
      unidadesTotales: unidadesAnualesGamaAlta + unidadesAnualesGamaMedia,
    };
  }, [totalEjecutivos, precioGamaAlta, precioGamaMedia]);

  // Calcular totales filtrados
  const totalesFiltrados = useMemo(() => {
    if (!resultados) return { gamaAlta: 0, gamaMedia: 0, total: 0, unidades: 0 };

    // Si ningún filtro está activo, mostrar todo
    const ningunFiltro = !filtroGamaAlta && !filtroGamaMedia;

    const gamaAlta = filtroGamaAlta || ningunFiltro ? resultados.gamaAlta.mercado : 0;
    const gamaMedia = filtroGamaMedia || ningunFiltro ? resultados.gamaMedia.mercado : 0;
    const unidadesAlta = filtroGamaAlta || ningunFiltro ? resultados.gamaAlta.unidadesAnuales : 0;
    const unidadesMedia = filtroGamaMedia || ningunFiltro ? resultados.gamaMedia.unidadesAnuales : 0;

    return {
      gamaAlta,
      gamaMedia,
      total: gamaAlta + gamaMedia,
      unidades: unidadesAlta + unidadesMedia,
    };
  }, [resultados, filtroGamaAlta, filtroGamaMedia]);

  // Calcular oportunidad por marca (crecimiento % sobre posición actual)
  const calcularOportunidadMarca = useCallback(
    (marca, porcentajeCrecimiento) => {
      if (!resultados) return { usuarios: 0, valorPotencial: 0, valorAnual: 0 };

      const marketShareActual = IPSOS_DATA.marcas[marca] || 0;
      // Crecimiento sobre la posición actual: si tiene 31.8% y crece 10%, pasa a 31.8% * 1.10 = 34.98%
      const nuevoMarketShare = marketShareActual * (1 + porcentajeCrecimiento / 100);

      const usuariosActuales = Math.round(resultados.smartphonesTotales * marketShareActual);
      const usuariosNuevos = Math.round(resultados.smartphonesTotales * nuevoMarketShare);
      const usuariosGanados = usuariosNuevos - usuariosActuales;

      // Valor promedio ponderado del mercado
      const precioPromedio = (precioGamaAlta + precioGamaMedia) / 2;
      const valorPotencial = usuariosGanados * precioPromedio;

      // Tasa de renovación anual ponderada (IPSOS):
      // Gama Alta: renueva cada 1-2 años → promedio 1.5 años → 66.7% anual
      // Gama Media: renueva cada 3+ años → promedio 4 años → 25% anual
      // Ponderación: 50% alta, 50% media → (0.667 + 0.25) / 2 ≈ 0.46
      const tasaRenovacionAnual = 0.46;
      const valorAnual = valorPotencial * tasaRenovacionAnual;

      return {
        usuarios: usuariosGanados,
        valorPotencial,
        valorAnual,
        marketShareActual: marketShareActual * 100,
        marketShareNuevo: nuevoMarketShare * 100,
      };
    },
    [resultados, precioGamaAlta, precioGamaMedia]
  );

  const getBlurClass = (gama) => {
    // Si ningún filtro está activo, no aplicar blur a nada
    const ningunFiltro = !filtroGamaAlta && !filtroGamaMedia;
    if (ningunFiltro) return "";

    if (gama === "alta" && !filtroGamaAlta) return styles.celdaBlur;
    if (gama === "media" && !filtroGamaMedia) return styles.celdaBlur;
    return "";
  };

  const generarTextoResumen = () => {
    const ningunFiltro = !filtroGamaAlta && !filtroGamaMedia;

    let gama = "";
    let resaltarGama = false;
    if (!ningunFiltro) {
      if (filtroGamaAlta && !filtroGamaMedia) {
        gama = "GAMA ALTA";
        resaltarGama = true;
      } else if (filtroGamaMedia && !filtroGamaAlta) {
        gama = "GAMA MEDIA";
        resaltarGama = true;
      }
    }

    const temporalidad = verMensual ? "CADA MES" : "CADA AÑO";

    return { gama, resaltarGama, temporalidad };
  };

  if (loading || edificios.length === 0) return <Loader />;

  return (
    <div className={styles.smartphonesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>Mercado Smartphones</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.smartphonesContent}>
        {/* Tabla resumen */}
        <div className={styles.tablaResumen}>
          <table className="table table-bordered table-sm">
            <tbody>
              {resultados && (
                <>
                  <tr>
                    <td className={cx("text-start", styles.celdaConEtiqueta)}>
                      Tienen smartphone personal
                      <span className={cx(styles.badgeIpsos, styles.etiquetaDerecha)}>
                        {(IPSOS_DATA.tieneSmartphone * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="text-end">{formatNumber(resultados.conSmartphone)}</td>
                  </tr>
                  <tr>
                    <td className={cx("text-start", styles.celdaConEtiqueta)}>
                      Tienen smartphone corporativo
                      <span className={cx(styles.badgeIpsos, styles.etiquetaDerecha)}>
                        {(IPSOS_DATA.tieneCorporativo * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="text-end">{formatNumber(resultados.conCorporativo)}</td>
                  </tr>
                  <tr className={styles.filaTotales}>
                    <td className="text-start">Smartphones en universo {pais === "Chile" ? "Wecast" : "Visiona"}</td>
                    <td className="text-end">{formatNumber(resultados.smartphonesTotales)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Filtros de análisis */}
        {resultados && (
          <div className={styles.filtrosContainer}>
            <div className={styles.filtrosCheckboxes}>
              <label>
                <input type="checkbox" checked={filtroGamaAlta} onChange={(e) => setFiltroGamaAlta(e.target.checked)} />
                Gama Alta
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={filtroGamaMedia}
                  onChange={(e) => setFiltroGamaMedia(e.target.checked)}
                />
                Gama Media
              </label>
              <label>
                <input type="checkbox" checked={verMensual} onChange={(e) => setVerMensual(e.target.checked)} />
                Ver mensual
              </label>
            </div>
          </div>
        )}

        {/* Tabla principal de resultados */}
        {resultados && (
          <div className={styles.tablaResultadosContainer}>
            <table className={cx("table table-bordered table-sm", styles.tablaResultados)}>
              <colgroup>
                <col style={{ width: "320px" }} />
                <col style={{ width: "320px" }} />
                <col style={{ width: "320px" }} />
                <col style={{ width: "320px" }} />
              </colgroup>
              <tbody>
                <tr>
                  <td className="text-start">Frecuencia de renovación</td>
                  <td className={cx("text-center", getBlurClass("alta"))}>
                    <span className={styles.badgeIpsos}>57.3% renueva cada 1-2 años</span>
                  </td>
                  <td className={cx("text-center", getBlurClass("media"))}>
                    <span className={styles.badgeIpsos}>42.8% renueva cada 3+ años</span>
                  </td>
                  {/* Conclusión con rowspan */}
                  <td rowSpan={6} className={styles.celdaConclusion}>
                    {(() => {
                      const { gama, resaltarGama, temporalidad } = generarTextoResumen();
                      return (
                        <div className={styles.resumenEjecutivo}>
                          <span className={styles.highlight}>{formatNumber(totalesFiltrados.unidades)}</span>
                          <br />
                          SMARTPHONES
                          {gama && (
                            <>
                              <br />
                              DE {resaltarGama ? <span className={styles.highlightSecondary}>{gama}</span> : gama}
                            </>
                          )}
                          <br />
                          SE RENUEVAN
                          <br />
                          <strong>{temporalidad}</strong>
                          <br />
                          EN LOS EDIFICIOS
                          <br />
                          SELECCIONADOS.
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Interpretación comercial</td>
                  <td className={cx("text-center", getBlurClass("alta"))}>Gama Alta</td>
                  <td className={cx("text-center", getBlurClass("media"))}>Gama Media</td>
                </tr>
                <tr>
                  <td className="text-start">Smartphones por categoría</td>
                  <td className={cx("text-center", getBlurClass("alta"))}>
                    {formatNumber(resultados.gamaAlta.smartphones)}
                  </td>
                  <td className={cx("text-center", getBlurClass("media"))}>
                    {formatNumber(resultados.gamaMedia.smartphones)}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Unidades renovadas por {verMensual ? "mes" : "año"}</td>
                  <td className={cx("text-center", getBlurClass("alta"))}>
                    {formatNumber(
                      verMensual ? resultados.gamaAlta.unidadesAnuales / 12 : resultados.gamaAlta.unidadesAnuales
                    )}
                  </td>
                  <td className={cx("text-center", getBlurClass("media"))}>
                    {formatNumber(
                      verMensual ? resultados.gamaMedia.unidadesAnuales / 12 : resultados.gamaMedia.unidadesAnuales
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Precio promedio</td>
                  <td className={cx("text-center", getBlurClass("alta"))}>
                    <select
                      value={precioGamaAlta}
                      onChange={(e) => setPrecioGamaAlta(Number(e.target.value))}
                      className="form-select form-select-sm mx-auto"
                      style={{ textAlign: "center", textAlignLast: "center" }}
                    >
                      {PRECIOS_GAMA_ALTA.map((p) => (
                        <option key={p} value={p}>
                          {formatUSD(p)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={cx("text-center", getBlurClass("media"))}>
                    <select
                      value={precioGamaMedia}
                      onChange={(e) => setPrecioGamaMedia(Number(e.target.value))}
                      className="form-select form-select-sm mx-auto"
                      style={{ textAlign: "center", textAlignLast: "center" }}
                    >
                      {PRECIOS_GAMA_MEDIA.map((p) => (
                        <option key={p} value={p}>
                          {formatUSD(p)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="text-start">Mercado {verMensual ? "mensual" : "anual"}</td>
                  <td className={cx("text-center", getBlurClass("alta"))}>
                    {formatUSD(verMensual ? resultados.gamaAlta.mercado / 12 : resultados.gamaAlta.mercado)}
                  </td>
                  <td className={cx("text-center", getBlurClass("media"))}>
                    {formatUSD(verMensual ? resultados.gamaMedia.mercado / 12 : resultados.gamaMedia.mercado)}
                  </td>
                </tr>
                <tr className={styles.filaTotales}>
                  <td className="text-start">Mercado total {verMensual ? "mensual" : "anual"}</td>
                  <td className="text-center" colSpan={3}>
                    {formatUSD(verMensual ? totalesFiltrados.total / 12 : totalesFiltrados.total)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Botón oportunidad por marca */}
            <div className={styles.oportunidadBtnContainer}>
              <button className={styles.oportunidadBtn} onClick={() => setShowOportunidadModal(true)}>
                Ver oportunidad por marca
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Y a este universo y mercado, debemos agregar:</strong>
              </p>
              <p>• Visitantes de los edificios que también tienen smartphones.</p>
              <p>• Oportunidades en el segmento B2B: renovación de flotas corporativas de dispositivos móviles.</p>
              <p>
                • Empresas de tecnología, telecomunicaciones y retail presentes en los edificios.
                <a href="/listado-empresas" target="_blank" rel="noopener noreferrer">
                  → Revisar empresas por edificio
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Seleccionar Edificios</h3>
              <button className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-striped table-hover table-sm">
                <thead className="table-dark">
                  <tr>
                    <th scope="col" className="text-center" style={{ width: "50px" }} />
                    <th scope="col" className="text-start">
                      EDIFICIO
                    </th>
                    <th scope="col" className="text-center">
                      UBICACIÓN
                    </th>
                    <th scope="col" className="text-center">
                      EJECUTIVOS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ created_at, edificio, id, ubicacion, personas }) => (
                    <tr
                      key={created_at}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-start">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Aplicar selección
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad por Marca */}
      {showOportunidadModal && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad de Mercado por Marca</h3>
              <button className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.marcaSelector}>
                <label>Selecciona tu marca:</label>
                <select
                  value={marcaSeleccionada}
                  onChange={(e) => setMarcaSeleccionada(e.target.value)}
                  className="form-select"
                >
                  {Object.keys(IPSOS_DATA.marcas).map((marca) => (
                    <option key={marca} value={marca}>
                      {marca}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.marketShareActual}>
                <h4>Market Share Actual (IPSOS)</h4>
                <div className={styles.marcasGrid}>
                  {Object.entries(IPSOS_DATA.marcas).map(([marca, share]) => (
                    <div
                      key={marca}
                      className={cx(
                        styles.marcaItem,
                        marca === marcaSeleccionada && styles.selected,
                        marca !== marcaSeleccionada && styles.marcaBlur
                      )}
                    >
                      <span className={styles.marcaNombre}>{marca}:</span>
                      <span>{(share * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.escenarios}>
                <h4>Escenario de captación para {marcaSeleccionada}</h4>
                {(() => {
                  const oportunidad = calcularOportunidadMarca(marcaSeleccionada, porcentajeCaptacion);
                  return (
                    <div className={styles.escenarioItem}>
                      <div className={styles.escenarioResultadoRow}>
                        <span className={styles.escenarioLabel}>Crecimiento:</span>
                        <select
                          value={porcentajeCaptacion}
                          onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                          className="form-select form-select-sm"
                          style={{ width: "100px" }}
                        >
                          {ESCENARIOS_CAPTACION.map((p) => (
                            <option key={p} value={p}>
                              +{p}%
                            </option>
                          ))}
                        </select>
                        <span>
                          Equipos adicionales:{" "}
                          <span className={styles.valor}>+{formatNumber(oportunidad.usuarios)}</span>
                        </span>
                      </div>
                      <div className={styles.escenarioValores}>
                        <div>
                          <span className={styles.valorLabel}>Valor potencial total:</span>
                          <span className={styles.valorDestacado}>{formatUSD(oportunidad.valorPotencial)}</span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Valor anual estimado:</span>
                          <span className={styles.valor}>{formatUSD(oportunidad.valorAnual)}</span>
                        </div>
                      </div>
                      <div className={styles.escenarioLeyenda}>
                        <p>
                          <strong>Valor potencial:</strong> Si todos los usuarios adicionales compraran un equipo.
                        </p>
                        <p>
                          <strong>Valor anual:</strong> Considerando frecuencia de renovación IPSOS (46% anual
                          ponderado).
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartPhones;
